import { type JSX } from 'react';
import { generatePath } from 'react-router';

import { useCachedData } from 'Components/Context/CachedDataContext';
import { ColumnConfig, ColumnType } from 'Components/Table/Columns';
import { FilterConfig, FilterType } from 'Components/Table/Components/Filters';
import { Table } from 'Components/Table/Table';
import { ACTION_DETAILS } from 'Config/PathTemplates';
import { ActionStatus, actionStatusComparator } from 'Models/Actions';
import { AssociatedAction } from 'Models/RiskRegister';

export interface AssociatedActionListingProps {
    actions: AssociatedAction[];
}

export const AssociatedActionListing = (props: AssociatedActionListingProps): JSX.Element => {
    const cachedData = useCachedData();

    const columns: ColumnConfig<AssociatedAction>[] = [
        {
            type: ColumnType.STRING,
            name: 'Action',
            getString: (action) => action.title,
            getOnClickTextAction: (action) => ({
                goToLocation: generatePath(ACTION_DETAILS, { action_id: action.id }),
            }),
        },
        {
            type: ColumnType.ENUM,
            name: 'Status',
            getString: (action) => action.status,
            sortComparator: (actionA, actionB) => actionStatusComparator(actionA.status, actionB.status),
        },
        {
            type: ColumnType.USER,
            name: 'Owner',
            getUserId: (action) => action.owner,
            users: cachedData.users,
        },
        {
            type: ColumnType.DATE,
            name: 'Last Updated',
            getDateString: (action) => action.last_updated,
        },
        {
            type: ColumnType.DATE,
            name: 'Due Date',
            getDateString: (action) => action.due_date,
        },
    ];

    const filters: FilterConfig<AssociatedAction>[] = [
        {
            type: FilterType.SINGLE_PROPERTY,
            id: 'statusFilter',
            label: 'Filter by Status',
            canSelectMultiple: true,
            options: [
                {
                    name: ActionStatus.OPEN,
                    value: ActionStatus.OPEN,
                },
                {
                    name: ActionStatus.CLOSED,
                    value: ActionStatus.CLOSED,
                },
            ],
            defaultSelections: [ActionStatus.OPEN],
            getFilterableDataForRow: (action) => [action.status],
        },
    ];

    return <Table columns={columns} defaultSortColumn="Action" filters={filters} getUniqueIdForRow={(action) => action.id} rowsData={props.actions} />;
};
